import { FC, useEffect, useState } from 'react'
import { DragLineItemProps as BaseDragLineItemProps } from './types'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import LineItem from './LineItem'

interface DragLineItemProps extends BaseDragLineItemProps {
  sorting: number | null | undefined
}

interface FnUpdateListOrderingItem {
  id: DragLineItemProps['id']
  sorting: number
}

interface DragLineListSortFieldProps {
  items: DragLineItemProps[]
  updateListOrdering: (items: FnUpdateListOrderingItem[]) => void
}

const DragLineListSortField: FC<DragLineListSortFieldProps> = ({
  items,
  updateListOrdering,
}) => {
  const [activeId, setActiveId] = useState<number | string | null>(null)
  const [sortedItems, setSortedItems] = useState<DragLineItemProps[]>([])

  useEffect(() => {
    let needSave = false

    let maxSorting = Math.max(...items.map((item) => item.sorting ?? -1))
    const processedItems = items.map((item, index) => {
      if (item.sorting === null || item.sorting === undefined) {
        needSave = true
        maxSorting += 1
        return { ...item, sorting: maxSorting }
      }
      return item
    })

    const initialSortedItems = [...processedItems].sort((a, b) => {
      const aValue = a.sorting ?? Infinity
      const bValue = b.sorting ?? Infinity
      return Number(aValue) - Number(bValue)
    })

    setSortedItems(initialSortedItems)

    if (needSave)
      updateListOrdering(
        initialSortedItems.map((item) => ({
          id: item.id,
          sorting: item.sorting,
        })) as FnUpdateListOrderingItem[],
      )
  }, [items, updateListOrdering])

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  )

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null)
    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = sortedItems.findIndex((item) => item.id === active.id)
    const newIndex = sortedItems.findIndex((item) => item.id === over.id)

    const newSortingValues = arrayMove(sortedItems, oldIndex, newIndex)

    const newSortedItems = newSortingValues.map((item, index) => ({
      ...item,
      sorting: sortedItems[index].sorting,
    }))

    const finalSortedItems = [...newSortedItems].sort((a, b) => {
      const aValue = a.sorting ?? Infinity
      const bValue = b.sorting ?? Infinity
      return Number(aValue) - Number(bValue)
    })

    setSortedItems(finalSortedItems)
    const updatedOrderData = finalSortedItems.map((item) => ({
      id: item.id,
      sorting: item.sorting,
    }))
    updateListOrdering(updatedOrderData as FnUpdateListOrderingItem[])
  }

  const activeItem = sortedItems.find((val) => val.id === activeId)

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sortedItems.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
      >
        {sortedItems.map((props) => (
          <LineItem key={props.id} {...props} />
        ))}
        <DragOverlay>
          {activeItem ? <LineItem {...activeItem} overlayItem={true} /> : null}
        </DragOverlay>
      </SortableContext>
    </DndContext>
  )
}

export default DragLineListSortField
